// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import * as React from 'react';
import {
  useMaybeLayoutContext,
  MediaDeviceMenu,
  useRoomContext,
  useIsRecording,
} from '@livekit/components-react';
// import styles from '@/styles/tkit/SettingsMenu.module.css';
import { CameraSettings } from './CameraSettings';
import { MicrophoneSettings } from './MicrophoneSettings';
import styles from '@/styles/call.settings.module.css';
import { cn } from '@/lib/utils.ts';

/**
 * @alpha
 */
export interface SettingsMenuProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * @alpha
 */
export function SettingsMenu(props: SettingsMenuProps) {
  const layoutContext = useMaybeLayoutContext();
  const room = useRoomContext();
  const recordingEndpoint = undefined; // process.env.VITE_LK_RECORD_ENDPOINT;

  const settings = React.useMemo(() => {
    return {
      media: { camera: true, microphone: true, label: 'Устройства медиа', speaker: true },
      recording: recordingEndpoint ? { label: 'Запись' } : undefined,
    };
  }, []);

  const tabs = React.useMemo(
    () => Object.keys(settings).filter((t) => t !== undefined) as Array<keyof typeof settings>,
    [settings],
  );
  const [activeTab, setActiveTab] = React.useState(tabs[0]);

  const isRecording = useIsRecording();
  const [initialRecStatus, setInitialRecStatus] = React.useState(isRecording);
  const [processingRecRequest, setProcessingRecRequest] = React.useState(false);

  React.useEffect(() => {
    if (initialRecStatus !== isRecording) {
      setProcessingRecRequest(false);
    }
  }, [isRecording, initialRecStatus]);

  const toggleRoomRecording = async () => {
    if (!recordingEndpoint) {
      throw TypeError('No recording endpoint specified');
    }
    if (room.isE2EEEnabled) {
      throw Error('Recording of encrypted meetings is currently not supported');
    }
    setProcessingRecRequest(true);
    setInitialRecStatus(isRecording);
    let response: Response;
    if (isRecording) {
      response = await fetch(recordingEndpoint + `/stop?roomName=${room.name}`);
    } else {
      response = await fetch(recordingEndpoint + `/start?roomName=${room.name}`);
    }
    if (response.ok) {
    } else {
      console.error(
        'Error handling recording request, check server logs:',
        response.status,
        response.statusText,
      );
      setProcessingRecRequest(false);
    }
  };

  return (
    <div className={styles.container} {...props}>
      <div className={styles.tabs}>
        {tabs.map(
          (tab) =>
            settings[tab] && (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(styles.tab, activeTab === tab && styles.tabActive)}
                aria-pressed={tab === activeTab}
              >
                {settings[tab]?.label}
              </button>
            )
        )}
      </div>
      <div className={styles.panel}>
        {activeTab === 'media' && (
          <>
            {settings.media && settings.media.camera && (
              <>
                <div className={styles.sectionTitle}>Камера</div>
                <section>
                  <CameraSettings />
                </section>
              </>
            )}
            {/*{settings.media && settings.media.microphone && (*/}
            {/*  <>*/}
            {/*    <h3>Microphone</h3>*/}
            {/*    <section>*/}
            {/*      <MicrophoneSettings />*/}
            {/*    </section>*/}
            {/*  </>*/}
            {/*)}*/}
            {/*{settings.media && settings.media.speaker && (*/}
            {/*  <>*/}
            {/*    <h3>Speaker & Headphones</h3>*/}
            {/*    <section className="lk-button-group">*/}
            {/*      <span className="lk-button">Audio Output</span>*/}
            {/*      <div className="lk-button-group-menu">*/}
            {/*        <MediaDeviceMenu kind="audiooutput"></MediaDeviceMenu>*/}
            {/*      </div>*/}
            {/*    </section>*/}
            {/*  </>*/}
            {/*)}*/}
          </>
        )}
        {activeTab === 'recording' && (
          <>
            <h3>Record Meeting</h3>
            <section>
              <p>
                {isRecording
                  ? 'Meeting is currently being recorded'
                  : 'No active recordings for this meeting'}
              </p>
              <button disabled={processingRecRequest} onClick={() => toggleRoomRecording()}>
                {isRecording ? 'Stop' : 'Start'} Recording
              </button>
            </section>
          </>
        )}
      </div>
      <div className={styles.footer}>
        <button
          className={styles.closeBtn}
          onClick={() => layoutContext?.widget.dispatch?.({ msg: 'toggle_settings' })}
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}
